/**
 * Messaging Row → Domain-Type Mappers
 *
 * The `conversations`/`messages` tables are snake_case with SPLIT unread counts
 * (`startup_unread_count` / `investor_unread_count`) and carry NO display names.
 * The client-facing `Conversation` / `Message` types (see `messaging-store.ts`)
 * are camelCase with a single viewer-relative `unreadCount` plus resolved names.
 *
 * These mappers are the single translation boundary between the DB shape and the
 * UI shape, so screens never hand-roll snake→camel conversions again.
 */

import type { Conversation, Message } from '@/lib/state/messaging-store';
import type { Tables } from './types';

/**
 * True when an error is a Postgres row-level-security denial — i.e. the
 * conversation-creation relationship gate rejected the INSERT (Postgres error
 * code 42501, surfaced by PostgREST as `code: '42501'`). Callers use this to
 * distinguish the EXPECTED "you don't have a relationship yet" case (show an
 * honest message, don't report to Sentry) from unexpected failures.
 */
export function isRelationshipDenied(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const code = (error as { code?: unknown }).code;
  if (code === '42501') return true;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.toLowerCase().includes('row-level security');
}

/**
 * A conversation row optionally joined with the counterparty display names.
 * `useConversations` embeds `startup_profiles(name)` and
 * `investor_profiles(name, firm)`; when the embed is absent we fall back to a
 * neutral label so the UI never renders `undefined`.
 */
export type ConversationRowWithNames = Tables<'conversations'> & {
  startup_profiles?: { name: string | null } | null;
  investor_profiles?: { name: string | null; firm: string | null } | null;
  /**
   * The CALLER's own unread row, embedded from `conversation_reads`.
   *
   * PostgREST returns an embed as an array, and `conversation_reads` is
   * RLS-scoped to `auth.uid()` — so this is 0 or 1 element, never another
   * person's count. Absent means nothing is owed, not "unknown".
   */
  conversation_reads?: { unread_count: number }[] | null;
};

/**
 * A message row carrying the sender's display name.
 *
 * `sender_name` is a REAL column on `messages`, written server-side by the
 * messages_set_sender_identity trigger (never by the client — the INSERT policy
 * constrains only sender_id, so a client-supplied name would be forgeable). It
 * stays optional here because rows predating that migration, and any row whose
 * sender has no name on their profile, legitimately carry NULL.
 */
export type MessageRowWithSender = Tables<'messages'> & {
  sender_name?: string | null;
};

/**
 * Display name for the investor side of a thread whose investor erased their
 * account (`investor_profile_id` is NULL — the FK is ON DELETE SET NULL so the
 * founder's half survives). Distinct from the generic 'Investor' fallback,
 * which means "name not loaded", not "gone".
 */
export const DELETED_INVESTOR_NAME = 'Deleted investor';

/** True when the investor side of this conversation no longer exists. */
export function isInvestorErased(conversation: { investorProfileId: string | null }): boolean {
  return conversation.investorProfileId === null;
}

/**
 * Map a DB conversation row to the UI `Conversation` type.
 *
 * Takes no viewer argument any more: unread is read from the caller's OWN
 * embedded `conversation_reads` row, which RLS already scopes to them, so
 * there is no longer a side to choose.
 */
export function mapConversationRow(row: ConversationRowWithNames): Conversation {
  // PER PERSON, from the caller's own conversation_reads row.
  //
  // This used to pick one of conversations.{startup,investor}_unread_count by
  // the viewer's SIDE. But a conversation belongs to a (startup, investor)
  // pair, so the whole founding team shared one counter: a co-founder opening
  // the thread cleared your badge for a message you never read. There is
  // deliberately NO fallback to the side counters here — falling back would
  // reinstate exactly that bug on any row whose embed is missing.
  const unreadCount = row.conversation_reads?.[0]?.unread_count ?? 0;

  return {
    id: row.id,
    startupId: row.startup_id,
    startupName: row.startup_profiles?.name ?? 'Startup',
    startupNameMissing: !row.startup_profiles?.name,
    investorId: row.investor_profile_id,
    investorProfileId: row.investor_profile_id,
    investorName:
      row.investor_profile_id === null
        ? DELETED_INVESTOR_NAME
        : row.investor_profiles?.name ?? 'Investor',
    // Derived read marks. Server-maintained from conversation_reads and readable
    // by participants, so the receipt costs no extra request. Not viewer-relative
    // here on purpose: the render site knows which side it is on and picks the
    // OTHER one.
    startupLastReadAt: row.startup_last_read_at ?? null,
    investorLastReadAt: row.investor_last_read_at ?? null,
    lastMessage: row.last_message ?? undefined,
    lastMessageTime: row.last_message_time ?? undefined,
    unreadCount,
    status: row.status === 'archived' ? 'archived' : 'active',
  };
}

/**
 * Map a DB message row to the UI `Message` type.
 *
 * `timestamp` is the raw ISO `created_at` — an instant, formatted for display
 * at the render site by formatMessageTime(), never pre-formatted here.
 *
 * `senderName` is the server-resolved name of the person who wrote it. The
 * sender_type fallback ("Investor" / "Startup") is now only reached for rows
 * predating the attribution migration or a sender with no name on file; it used
 * to be reached by EVERY row, which is why a startup's two founders appeared in
 * an investor's thread as one anonymous voice.
 */
export function mapMessageRow(row: MessageRowWithSender): Message {
  const senderType: 'startup' | 'investor' =
    row.sender_type === 'investor' ? 'investor' : 'startup';

  const senderRole =
    row.sender_role === 'founder' ||
    row.sender_role === 'co-founder' ||
    row.sender_role === 'investor_relations' ||
    row.sender_role === 'other'
      ? row.sender_role
      : undefined;

  return {
    id: row.id,
    conversationId: row.conversation_id,
    senderId: row.sender_id,
    senderName:
      row.sender_name ?? (senderType === 'investor' ? 'Investor' : 'Startup'),
    senderType,
    senderRole,
    text: row.text,
    timestamp: row.created_at ?? '',
  };
}

/**
 * 119-07: fill the name of a conversation whose company profile is unreadable
 * (a SEALED company) from its public card — the card's name only, no sealed
 * data. Rows with a readable name, or with no card, are returned unchanged.
 */
export function withPublicCardNames<T extends { startupId: string; startupName: string; startupNameMissing?: boolean }>(
  conversations: readonly T[],
  cards: ReadonlyMap<string, { name: string | null }> | undefined,
): T[] {
  if (!cards || cards.size === 0) return [...conversations];
  return conversations.map((c) => {
    if (!c.startupNameMissing) return c;
    const name = cards.get(c.startupId.toLowerCase())?.name;
    return name ? { ...c, startupName: name } : c;
  });
}
